import type { ReportTypeKey } from './reports.js';

/**
 * **Analysis frames** (event graph design §2, phase 2): a question a vertical
 * asks of every attendee of an event. The event module is generic and names no
 * frame; a vertical asks a frame only as a factor of one of its analyses
 * (below), and a vertical whose analyses have no factors asks none (design
 * ruling 10).
 *
 * A frame rates each reading of an attendee's name (the scan's candidates) on
 * its tiers. The attendee's own tier is computed from those ratings and from
 * what the office has said about who the attendee is: `unresolved` ("To
 * check") while a reading carries something relevant and nobody has said which
 * reading is them. When nothing ties a reading to the attendee (the scan found
 * nothing, only namesakes rated clear, or the office said none of them is the
 * attendee), a frame with an `unplaced` tier reads that ("No flags"): a
 * screening frame never calls someone clear it could not identify. Any other
 * frame reads its clear tier there, which says what it means ("No stated
 * match"). A screening frame's `clear` needs a reading tied to the attendee.
 */
export const EVENT_FRAME_KEYS = [
  'disruption',
  'goal_fit',
  'public_profile',
  'money_event',
  'diligence',
] as const;
export type EventFrameKey = (typeof EVENT_FRAME_KEYS)[number];

export interface EventFrameTier {
  readonly key: string;
  readonly label: string;
}

export interface EventFrameDef {
  readonly key: EventFrameKey;
  readonly label: string;
  /** Most concerning first. */
  readonly tiers: readonly EventFrameTier[];
  /** The tier meaning every reading of the name was rated clear. */
  readonly clear: string;
  /** The tier meaning a relevant reading waits on an identity answer. */
  readonly unresolved: string;
  /**
   * A screening frame's tier for an attendee nothing ties a reading to and
   * nothing flagged: listed in `tiers` for display, never offered to the scan
   * to rate a reading with. Null where the clear tier already says as much.
   */
  readonly unplaced: string | null;
  /**
   * The analysis inputs this frame is rated against (e.g. `objectives`). Empty
   * is a **fixed** frame: one rating per reading, shared by every analysis that
   * lists it. Otherwise a **parameterized** frame: a rating is only true for the
   * input it was made with, so it belongs to the analysis run that supplied it
   * (docs/notes/2026-09-24-analysis-ratings-spec.md).
   */
  readonly inputs: readonly string[];
  /**
   * Web searches this frame may add for one attendee already scanned, when it
   * rates them from what is stored. On the frame, not the analysis: a fixed
   * frame's rating is shared, so it is bought the same way whichever analysis
   * starts the scan. Not a research setting, so no operator tuning moves it.
   */
  readonly rate_search_cap: number;
}

/**
 * **Signs of disruption** (security only; design ruling 8): a history of
 * disrupting or protesting events, public statements about the speaker or the
 * event, organizing against it, membership of a group known for disruption.
 * Disagreement alone is not a sign.
 */
const DISRUPTION: EventFrameDef = Object.freeze({
  key: 'disruption',
  label: 'Signs of disruption',
  tiers: Object.freeze([
    { key: 'flagged', label: 'Flagged' },
    { key: 'unknown', label: 'To check' },
    { key: 'no_flags', label: 'No flags' },
    { key: 'clear', label: 'Clear' },
  ]),
  clear: 'clear',
  unresolved: 'unknown',
  unplaced: 'no_flags',
  inputs: Object.freeze([]),
  // The three searches the frame adds to a full scan.
  rate_search_cap: 3,
});

/**
 * **Matches your goals** (security, business, personal;
 * docs/notes/2026-09-24-event-analyses-per-vertical-spec.md): whether a cited
 * passage states a role, employer, sector, project, holding, cause or public
 * position of the reading that bears directly on a goal the office entered.
 * Parameterized: a rating is true only for the goals it was made against. Its
 * words are shared by every vertical that asks it; each analysis names it.
 */
const GOAL_FIT: EventFrameDef = Object.freeze({
  key: 'goal_fit',
  label: 'Matches your goals',
  tiers: Object.freeze([
    { key: 'match', label: 'Matches' },
    { key: 'unknown', label: 'To check' },
    { key: 'clear', label: 'No stated match' },
  ]),
  clear: 'clear',
  unresolved: 'unknown',
  unplaced: null,
  inputs: Object.freeze(['goals']),
  rate_search_cap: 2,
});

/**
 * **Public profile** (club): a public role, an honor, press work, a public
 * figure's standing, or a stated tie to the club or its members. Never wealth,
 * family or suitability for membership.
 */
const PUBLIC_PROFILE: EventFrameDef = Object.freeze({
  key: 'public_profile',
  label: 'Public profile',
  tiers: Object.freeze([
    { key: 'public', label: 'Public profile' },
    { key: 'unknown', label: 'To check' },
    { key: 'clear', label: 'Nothing public found' },
  ]),
  clear: 'clear',
  unresolved: 'unknown',
  unplaced: null,
  inputs: Object.freeze([]),
  rate_search_cap: 2,
});

/**
 * **Recent money event** (wealth): a company sale, IPO or acquisition where the
 * person was a founder or executive, an inheritance, a large sale or gift, or
 * retirement from a senior role, as a cited passage states it. Never an amount
 * or a guess at wealth.
 */
const MONEY_EVENT: EventFrameDef = Object.freeze({
  key: 'money_event',
  label: 'Recent money event',
  tiers: Object.freeze([
    { key: 'stated', label: 'Stated' },
    { key: 'unknown', label: 'To check' },
    { key: 'clear', label: 'None stated' },
  ]),
  clear: 'clear',
  unresolved: 'unknown',
  unplaced: null,
  inputs: Object.freeze([]),
  rate_search_cap: 3,
});

/**
 * **Diligence concerns** (wealth): a regulatory action, sanction, criminal
 * charge or conviction, fraud finding, industry bar or judgment about business
 * conduct, as a cited passage states it of the reading.
 */
const DILIGENCE: EventFrameDef = Object.freeze({
  key: 'diligence',
  label: 'Diligence concerns',
  tiers: Object.freeze([
    { key: 'flagged', label: 'Flagged' },
    { key: 'unknown', label: 'To check' },
    { key: 'no_flags', label: 'No flags' },
    { key: 'clear', label: 'Clear' },
  ]),
  clear: 'clear',
  unresolved: 'unknown',
  unplaced: 'no_flags',
  inputs: Object.freeze([]),
  rate_search_cap: 2,
});

export const EVENT_FRAMES: Readonly<Record<EventFrameKey, EventFrameDef>> = Object.freeze({
  disruption: DISRUPTION,
  goal_fit: GOAL_FIT,
  public_profile: PUBLIC_PROFILE,
  money_event: MONEY_EVENT,
  diligence: DILIGENCE,
});

/** A frame by its key as the wire carries it; undefined for a key this build does not know. */
export function eventFrameOf(key: string): EventFrameDef | undefined {
  return (EVENT_FRAME_KEYS as readonly string[]).includes(key)
    ? EVENT_FRAMES[key as EventFrameKey]
    : undefined;
}

/** The tiers the scan may rate a reading on: every tier but the computed `unplaced` one. */
export function ratedTiers(frame: EventFrameDef): readonly EventFrameTier[] {
  return frame.tiers.filter((tier) => tier.key !== frame.unplaced);
}

/** The tier key a list filter uses for attendees a frame has not rated. */
export const NOT_ASSESSED = 'not_assessed';

/**
 * **An analysis the office can run on an event** (Soroush, 2026-09-24,
 * docs/notes/2026-09-24-event-analysis-flow-spec.md): what the event's Analysis
 * tab lists, one entry per analysis the org's vertical offers, each shown as its
 * run offer or as its results. An analysis is made of **factors**, each one of
 * the frames the attendee scan rates, and it produces one written **report**.
 * An analysis with factors needs the scan; one without goes straight to its
 * report. A vertical adds an analysis or a factor here; it never adds a branch
 * to the page.
 */
export interface EventAnalysisDef {
  readonly key: string;
  readonly label: string;
  /** One line under the run button, shown before the analysis has run. */
  readonly offer: string;
  /** Frames the analysis presents, in order; any frame means it needs the attendee scan. */
  readonly factors: readonly EventFrameKey[];
  /** The report type that writes it up. */
  readonly report: ReportTypeKey;
  /**
   * What the office enters when it runs the analysis, for its parameterized
   * factors to be rated against. Every input a factor takes is one of these.
   */
  readonly inputs: readonly EventAnalysisInputDef[];
}

export interface EventAnalysisInputDef {
  readonly key: string;
  readonly label: string;
}

const SECURITY_CROWD_SUMMARY: EventAnalysisDef = Object.freeze({
  key: 'crowd_summary',
  label: 'Crowd summary',
  offer:
    'Who on the attendee list shows signs of disruption, who could not be told apart, and who is clear.',
  factors: Object.freeze(['disruption'] as const),
  report: 'crowd_summary',
  inputs: Object.freeze([]),
});

const SECURITY_PRINCIPAL_FIT: EventAnalysisDef = Object.freeze({
  key: 'principal_fit',
  label: 'Principal fit',
  offer:
    "Who on the attendee list bears on the principal's objectives for this event, who could not be told apart, and everyone else.",
  factors: Object.freeze(['goal_fit'] as const),
  report: 'principal_briefing',
  inputs: Object.freeze([{ key: 'goals', label: "The principal's objectives at this event" }]),
});

const CLUB_EVENT_ADVANCE: EventAnalysisDef = Object.freeze({
  key: 'event_advance',
  label: 'Event advance',
  offer:
    'Who is coming, who already knows whom, who might be introduced, and a short note on each person.',
  factors: Object.freeze([] as const),
  report: 'event_advance',
  inputs: Object.freeze([]),
});

const CLUB_GUESTS_TO_KNOW: EventAnalysisDef = Object.freeze({
  key: 'guests_to_know',
  label: 'Attendees to know',
  offer:
    "Who on the list has a public profile or a stated tie to the club, who bears on the club's goals for the event, who could not be told apart, and everyone else.",
  factors: Object.freeze(['public_profile', 'goal_fit'] as const),
  report: 'guests_to_know',
  inputs: Object.freeze([{ key: 'goals', label: 'What the club wants from this event' }]),
});

const WEALTH_PROSPECTS: EventAnalysisDef = Object.freeze({
  key: 'prospects',
  label: 'Prospects in the room',
  offer:
    'Who on the list has a recent money event or a diligence concern stated in public sources, who matches what you want from the event, and everyone else.',
  factors: Object.freeze(['money_event', 'diligence', 'goal_fit'] as const),
  report: 'prospects_summary',
  inputs: Object.freeze([{ key: 'goals', label: 'What you want from this event' }]),
});

/** Business and personal ask the same question in the same words. */
const WHO_TO_MEET: EventAnalysisDef = Object.freeze({
  key: 'who_to_meet',
  label: 'Who to meet',
  offer:
    'Who on the attendee list matches what you want from this event, who could not be told apart, and everyone else.',
  factors: Object.freeze(['goal_fit'] as const),
  report: 'meeting_list',
  inputs: Object.freeze([{ key: 'goals', label: 'What you want from this event' }]),
});

const BUSINESS_COMPANIES_IN_ROOM: EventAnalysisDef = Object.freeze({
  key: 'companies_in_room',
  label: 'Companies in the room',
  offer:
    'The attendee list grouped by company, and what is on record about the companies and people you already know.',
  factors: Object.freeze([] as const),
  report: 'companies_in_room',
  inputs: Object.freeze([]),
});

/**
 * Which analyses each vertical offers, in menu order
 * (docs/notes/2026-09-24-event-analyses-per-vertical-spec.md). Every vertical is
 * listed, so a vertical without analyses would be a decision made here and not
 * an omission.
 */
export const EVENT_ANALYSES_BY_VERTICAL: Readonly<Record<string, readonly EventAnalysisDef[]>> =
  Object.freeze({
    security: Object.freeze([SECURITY_CROWD_SUMMARY, SECURITY_PRINCIPAL_FIT]),
    club: Object.freeze([CLUB_EVENT_ADVANCE, CLUB_GUESTS_TO_KNOW]),
    business: Object.freeze([WHO_TO_MEET, BUSINESS_COMPANIES_IN_ROOM]),
    personal: Object.freeze([WHO_TO_MEET]),
    wealth: Object.freeze([WEALTH_PROSPECTS]),
  });

export function eventAnalysesFor(vertical: string): EventAnalysisDef[] {
  return [...(EVENT_ANALYSES_BY_VERTICAL[vertical] ?? [])];
}

/** An analysis with factors is rated by the attendee scan; one without is only its report. */
export function needsScan(analysis: EventAnalysisDef): boolean {
  return analysis.factors.length > 0;
}

/**
 * **Which frames each vertical's scan rates**: the union of its analyses'
 * factors, in menu order. Derived, so "what does this vertical scan for" has
 * one source of truth, the menu. A vertical whose menu needs no scan has none,
 * and the server refuses to start a scan there (`SCAN_NOT_OFFERED`).
 */
export const EVENT_FRAMES_BY_VERTICAL: Readonly<Record<string, readonly EventFrameKey[]>> =
  Object.freeze(
    Object.fromEntries(
      Object.entries(EVENT_ANALYSES_BY_VERTICAL).map(([vertical, analyses]) => [
        vertical,
        Object.freeze([...new Set(analyses.flatMap((one) => one.factors))]),
      ]),
    ),
  );

export function eventFramesFor(vertical: string): EventFrameDef[] {
  return (EVENT_FRAMES_BY_VERTICAL[vertical] ?? []).map((key) => EVENT_FRAMES[key]);
}

/** Whether any analysis the vertical offers needs the attendee scan. */
export function eventScanOffered(vertical: string): boolean {
  return eventAnalysesFor(vertical).some(needsScan);
}

/** Whether a frame's ratings belong to one analysis run (it takes an input). */
export function isParameterized(frame: Pick<EventFrameDef, 'inputs'>): boolean {
  return frame.inputs.length > 0;
}

/**
 * How `event_graph_node.assessed_frames` marks a frame as rated: the key for a
 * fixed frame, `<key>@<analysis run>` for a parameterized one, so a rating made
 * for one input never passes for another's.
 */
export function assessedKey(frame: string, analysisRunId: string | null): string {
  return analysisRunId ? `${frame}@${analysisRunId}` : frame;
}
